// Progetto Arca's layer over `@agent-runtime/client`.
//
// The shared client folds the runner transcript (messages, forms, input
// answers, turn flags, dedupe) and the tool timeline the rail groups per
// turn, and maps refusals to stable codes. This module keeps what is Arca's
// own: the Italian copy per error code and the retry offers.
import {
  applyEvent as applySharedEvent,
  chatError as sharedChatError,
  currentCommentary,
  errorFromResponse,
  initialChatState as sharedInitialState,
  isObject,
  replayCursor,
  freezeRenderedInputs as freezeSharedInputs,
  type ChatError as SharedChatError,
  type ChatErrorCode as SharedChatErrorCode,
  type ChatEvent,
  type ChatState as SharedChatState,
  type InputRecord,
  type LocalEvent,
} from '@agent-runtime/client'

export {
  assistantSegments,
  createPrewarmGate,
  createSseParser,
  optimisticUserEvent,
  publishedInputSetIds as inputSetIds,
  readEventStream,
  reconnectDelay,
  retryConflictResponses,
  sessionStatusFromPayload,
  type AssistantSegment,
  type Attachment as ChatAttachment,
  type ChatMessage,
  type InputAnswer,
  type InputForm,
  type ReconnectPolicy,
  type SessionStatus,
  type ToolActivity,
  type TurnMode,
} from '@agent-runtime/client'

// Codes the app raises for its own HTTP calls, next to the shared ones.
export type AppErrorCode =
  | 'send_failed'
  | 'reattach_failed'
  | 'input_failed'
  | 'cancel_failed'

export type ChatErrorCode = SharedChatErrorCode | AppErrorCode

export type ChatError = { code: ChatErrorCode; message: string; retryable: boolean }

// One sentence per code, in the user's language, and whether the notice
// offers a retry. The shared library decides the code; the product decides
// the copy and the offer.
export const CHAT_ERROR_COPY: Record<ChatErrorCode, { message: string; retryable: boolean }> = {
  request_already_admitted: {
    message: 'La richiesta è già stata ricevuta. Ricarica la conversazione per vedere la risposta.',
    retryable: false,
  },
  request_admission_unconfirmed: {
    message: 'La ricezione della richiesta non è ancora confermata. Ricarica la conversazione prima di inviarla di nuovo.',
    retryable: false,
  },
  request_payload_conflict: {
    message: 'La richiesta non corrisponde a quella già ricevuta. Ricarica la conversazione.',
    retryable: false,
  },
  admission_unavailable: {
    message: 'Non è stato possibile ricevere la richiesta. Riprova tra poco.',
    retryable: true,
  },
  runner_updating: {
    message: 'L’agente si sta aggiornando. Riprova tra poco.',
    retryable: true,
  },
  busy: {
    message:
      'Un altro turno è già in corso per questa conversazione. Attendi che finisca, poi riprova.',
    retryable: true,
  },
  harness_mismatch: {
    message:
      'Questa conversazione è legata a un altro runtime e non può continuare qui. Apri una nuova chat.',
    retryable: false,
  },
  pointer_invalid: {
    message: 'Questa conversazione non può essere ripresa. Apri una nuova chat.',
    retryable: false,
  },
  resume_failed: {
    message: 'Questa conversazione non può essere ripresa. Apri una nuova chat.',
    retryable: false,
  },
  model_harness_mismatch: {
    message: 'Il modello configurato non è disponibile.',
    retryable: false,
  },
  model_required: {
    message: 'Il modello configurato non è disponibile.',
    retryable: false,
  },
  harness_unavailable: {
    message: 'L’agente non è disponibile in questo momento. Riprova tra poco.',
    retryable: true,
  },
  worker_unavailable: {
    message: 'L’agente non è disponibile in questo momento. Riprova tra poco.',
    retryable: true,
  },
  unavailable: {
    message: 'L’agente non è disponibile in questo momento. Riprova tra poco.',
    retryable: true,
  },
  input_disabled: {
    message: 'Le risposte guidate non sono attive su questo runner.',
    retryable: false,
  },
  unauthorized: {
    message: 'Accesso al runner negato. Ricarica la pagina e riprova.',
    retryable: false,
  },
  not_found: {
    message: 'Conversazione non trovata sul runner. Apri una nuova chat.',
    retryable: false,
  },
  invalid_request: {
    message: 'Richiesta non valida: è un errore dell’applicazione, segnalalo.',
    retryable: false,
  },
  validation: {
    message: 'Richiesta non valida: è un errore dell’applicazione, segnalalo.',
    retryable: false,
  },
  runner_error: {
    message: 'L’agente non ha accettato la richiesta. Riprova tra poco.',
    retryable: true,
  },
  turn_failed: { message: 'Luma non è riuscita a completare la risposta. Riprova tra qualche minuto.', retryable: true },
  turn_cancelled: { message: 'Risposta interrotta.', retryable: false },
  stream_lost: {
    message:
      'Connessione interrotta durante la risposta e non ripristinata. Riprova o ricarica la conversazione.',
    retryable: true,
  },
  network: {
    message: 'L’agente non è raggiungibile. Riprova tra poco.',
    retryable: true,
  },
  unknown: {
    message: 'L’agente non ha accettato la richiesta. Riprova tra poco.',
    retryable: true,
  },
  send_failed: {
    message: 'Connessione interrotta: invio non confermato. Controlla la conversazione prima di riprovare.',
    retryable: true,
  },
  reattach_failed: {
    message:
      'Connessione interrotta durante la risposta e non ripristinata. Riprova o ricarica la conversazione.',
    retryable: true,
  },
  input_failed: { message: 'Risposta non registrata. Riprova.', retryable: false },
  cancel_failed: { message: 'Impossibile interrompere la risposta. Riprova.', retryable: false },
}

export function chatError(code: ChatErrorCode): ChatError {
  return { code, ...CHAT_ERROR_COPY[code] }
}

export function chatErrorCode(value: unknown): ChatErrorCode {
  return typeof value === 'string' && value in CHAT_ERROR_COPY
    ? (value as ChatErrorCode)
    : 'runner_error'
}

// The error a failed backend response means. The backend relays the runner's
// refusal (`{detail: {error, message}}`) or answers with its own code
// (`{error: 'send_failed'}`); the shared mapping reads both.
export function chatErrorFromResponse(status: number, body: unknown): ChatError {
  const appCode = appErrorCode(body)
  if (appCode) return chatError(appCode)
  const error = errorFromResponse(status, body)
  return chatError(error ? chatErrorCode(error.code) : 'runner_error')
}

function appErrorCode(body: unknown): AppErrorCode | null {
  const detail = isObject(body) && isObject(body.detail) ? body.detail : body
  const code = isObject(detail) ? detail.error : null
  return typeof code === 'string' && code in CHAT_ERROR_COPY && !isSharedCode(code)
    ? (code as AppErrorCode)
    : null
}

const APP_CODES: ReadonlySet<string> = new Set<AppErrorCode>([
  'send_failed',
  'reattach_failed',
  'input_failed',
  'cancel_failed',
])

function isSharedCode(code: string): code is SharedChatErrorCode {
  return code in CHAT_ERROR_COPY && !APP_CODES.has(code)
}

export type ChatState = Omit<SharedChatState, 'error'> & { error: ChatError | null }

export function initialChatState(): ChatState {
  return { ...sharedInitialState(), error: null }
}

// The uuid to resume after: the last user or assistant message the client holds.
// A pending message never reached the transcript: its optimistic uuid is not
// a position the server can resume from.
export function resumeCursor(state: ChatState): string | null {
  return replayCursor(
    state.messages
      .filter((message) => !message.pending)
      .map((message) => ({ type: message.role, uuid: message.uuid })),
  )
}

// The event that marks every rendered input set frozen once a send went through.
export function freezeRenderedInputs(state: ChatState): LocalEvent {
  return freezeSharedInputs(sharedView(state))
}

export function inputRecordEvent(record: unknown): LocalEvent | null {
  if (!isObject(record)) return null
  if (typeof record.request_id !== 'string' || typeof record.set_id !== 'string') return null
  return { type: 'input_record', record: record as unknown as InputRecord }
}

// ---------------------------------------------------------------------------
// Reducer: the shared fold, then Arca's copy on top.

// The open turn's latest finished note. The runtime moves a note out of the
// transcript when it finishes, so the rail keeps it on screen until the next
// note or the answer starts.
export function liveCommentary(state: ChatState): string | null {
  return currentCommentary(sharedView(state))
}

export function applyEvent(state: ChatState, event: unknown): ChatState {
  if (!isObject(event) || typeof event.type !== 'string') return state
  const view = sharedView(state)
  const shared = applySharedEvent(view, sharedEvent(event))
  if (shared === view) return state
  const next: ChatState = { ...shared, error: arcaError(shared.error) }
  if (event.type !== 'end') return next
  // The turn is over, so its mode is too; a cancelled turn tells the user.
  return {
    ...next,
    turnMode: null,
    error: next.endStatus === 'cancelled' ? chatError('turn_cancelled') : next.error,
  }
}

function sharedView(state: ChatState): SharedChatState {
  return { ...state, error: state.error ? sharedErrorFor(state.error.code) : null }
}

// The backend relays refusals as `error` frames with the runner's code; the
// shared reducer only reads `reason`, so hand it the error directly.
function sharedEvent(event: Record<string, unknown>): ChatEvent {
  if (event.type === 'error' && typeof event.code === 'string') {
    return { type: 'chat_error', error: sharedErrorFor(chatErrorCode(event.code)) }
  }
  if (event.type === 'chat_error' && isObject(event.error) && typeof event.error.code === 'string') {
    return { type: 'chat_error', error: sharedErrorFor(chatErrorCode(event.error.code)) }
  }
  return event as ChatEvent
}

// App-only codes travel through the shared state as `unknown` and are
// restored from the copy table on the way out.
const appCodeCarrier = new WeakMap<SharedChatError, ChatErrorCode>()

function sharedErrorFor(code: ChatErrorCode): SharedChatError {
  if (isSharedCode(code)) return sharedChatError(code)
  const carrier = sharedChatError('unknown')
  appCodeCarrier.set(carrier, code)
  return carrier
}

function arcaError(error: SharedChatError | null): ChatError | null {
  if (!error) return null
  return chatError(appCodeCarrier.get(error) ?? chatErrorCode(error.code))
}
